import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { wishlistService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ShoppingCart, CheckCircle, Trash2, ExternalLink, ShieldUser } from 'lucide-react';
import { sanitizeUrl } from '../utils/sanitize';
import { useModal } from '../context/ModalContext';

const FamilyWishlists = () => {
  const modal = useModal();
  const navigate = useNavigate();
  const [wishlists, setWishlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const hasPromptedRef = useRef(false);

  // State for buyer selection modal
  const [buyTarget, setBuyTarget] = useState(null); // { wishlistId, itemId, itemName }
  const [selectedBuyerId, setSelectedBuyerId] = useState('');

  const fetchFamilyWishlists = async () => {
    try {
      const res = await wishlistService.getFamily();
      setWishlists(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (hasPromptedRef.current) return;
    hasPromptedRef.current = true;

    const init = async () => {
      if (user.children && user.children.length > 0) {
        const confirmed = await modal.confirm({
          title: 'Spoiler Warning!',
          message: 'You are about to view all wishlists, which will reveal the bought status of any children you have. You might want to ensure they are not looking before proceeding!',
          variant: 'info',
          confirmText: 'Continue',
          cancelText: 'Return to Dashboard'
        });
        if (!confirmed) {
          navigate('/dashboard');
          return;
        }
      }
      fetchFamilyWishlists();
    };

    init();
  }, [user, navigate, modal]);

  const handleBuyClick = (wishlistId, item) => {
    // If user has children, open selection modal
    if (user && user.children && user.children.length > 0) {
      // Exclude children whose wishlist is the target wishlist (a child cannot buy for themselves)
      const eligibleBuyers = [
        { id: user.id, name: `${user.name} (Myself)` },
        ...user.children
          .filter(c => c.id !== wishlistId)
          .map(c => ({ id: c.id, name: c.name }))
      ];

      // If only parent is eligible (e.g. buying on child's own wishlist), buy directly as parent or show choices
      if (eligibleBuyers.length === 1) {
        executeBuy(wishlistId, item.id, user.id);
        return;
      }

      setBuyTarget({ wishlistId, itemId: item.id, itemName: item.name, eligibleBuyers });
      setSelectedBuyerId(user.id);
    } else {
      executeBuy(wishlistId, item.id, user.id);
    }
  };

  const executeBuy = async (wishlistId, itemId, buyerId) => {
    try {
      await wishlistService.buyItem(wishlistId, itemId, buyerId);
      setBuyTarget(null);
      fetchFamilyWishlists();
    } catch (err) {
      const errMsg = err.response?.data?.msg || 'Error marking item as bought';
      modal.alert({ title: 'Error', message: errMsg, variant: 'danger' });
    }
  };

  const handleUnbuy = async (wishlistId, itemId) => {
    try {
      await wishlistService.unbuyItem(wishlistId, itemId);
      fetchFamilyWishlists();
    } catch (err) {
      modal.alert({ title: 'Error', message: 'Error clearing item status', variant: 'danger' });
    }
  };

  const handleDeleteList = async (userId) => {
    const confirmed = await modal.confirm({
      title: 'Delete Wishlist',
      message: "Are you sure you want to delete this member's wishlist?",
      variant: 'danger',
      confirmText: 'Delete'
    });
    if (confirmed) {
      try {
        await wishlistService.deleteMemberWishlist(userId);
        fetchFamilyWishlists();
      } catch (err) {
        modal.alert({ title: 'Error', message: 'Error deleting wishlist', variant: 'danger' });
      }
    }
  };

  if (loading) return <div className="text-center mt-10 text-xl font-bold">Gathering Wishlists...</div>;

  return (
    <div className="max-w-4xl mx-auto mt-10 px-4">
      <h1 className="text-3xl font-bold text-xmas-red mb-8">Family Wishlists</h1>

      {/* Modal for selecting who is buying the gift */}
      {buyTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-xl font-bold text-gray-800">Who is buying this gift?</h3>
            <p className="text-sm text-gray-600">
              Select who should be recorded as buying <span className="font-semibold text-xmas-red">"{buyTarget.itemName}"</span>:
            </p>
            <div className="space-y-2 max-h-60 overflow-y-auto border p-3 rounded">
              {buyTarget.eligibleBuyers.map((b) => (
                <label key={b.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer border border-transparent hover:border-gray-200">
                  <input
                    type="radio"
                    name="buyerSelect"
                    value={b.id}
                    checked={selectedBuyerId === b.id}
                    onChange={(e) => setSelectedBuyerId(e.target.value)}
                    className="text-xmas-red focus:ring-xmas-red h-4 w-4"
                  />
                  <span className="font-medium text-gray-700">{b.name}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setBuyTarget(null)}
                className="btn-secondary py-2 px-4"
              >
                Cancel
              </button>
              <button
                onClick={() => executeBuy(buyTarget.wishlistId, buyTarget.itemId, selectedBuyerId)}
                className="btn-primary py-2 px-4"
              >
                Confirm Purchase
              </button>
            </div>
          </div>
        </div>
      )}

      {wishlists.length === 0 ? (
        <div className="card text-center py-12">
            <p className="text-xl text-gray-500 italic">No one has shared their wishlist yet. Remind them to share theirs soon!</p>
        </div>
      ) : (
        <div className="space-y-12">
          {wishlists.map((groupData) => {
            const isUserAdminOfThisGroup = groupData.administrator === user.id;

            return (
              <div key={groupData.groupId} className="border-b-2 border-gray-200 pb-8 last:border-b-0">
                <h2 className="text-2xl font-bold text-xmas-red mb-6 bg-xmas-red/5 p-3 rounded">
                  Group: {groupData.groupName}
                </h2>

                {groupData.wishlists.length === 0 ? (
                  <p className="italic text-gray-500 ml-4">No shared wishlists in this group.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {groupData.wishlists.map((list) => (
                      <div key={list.userId} className="card relative">
                        {isUserAdminOfThisGroup && (
                          <button
                              onClick={() => handleDeleteList(list.user.id)}
                              className="absolute top-4 right-4 text-gray-400 hover:text-red-600"
                              title="Delete member's list (Admin Only)"
                          >
                              <Trash2 size={20}/>
                          </button>
                        )}
                        <h3 className="text-2xl font-bold text-xmas-green mb-4 border-b pb-2 flex items-center gap-2">
                          {list.user.isChild ? <ShieldUser className="text-xmas-red" size={24} /> : null}
                          <span>
                            {list.user.name}'s List
                            {list.user.isChild && (
                              <span className="text-sm font-normal text-gray-500 block">
                                (Child of {list.user.parentName || "Parent"})
                              </span>
                            )}
                          </span>
                        </h3>
                        <div className="space-y-4">
                          {list.items.map((item) => {
                            const safeLink = sanitizeUrl(item.link);
                            return (
                              <div key={item.id} className="p-3 border rounded-lg bg-gray-50 flex flex-col gap-2">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <span className={`font-bold ${item.boughtBy ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                                            {item.name}
                                        </span>
                                        {safeLink && (
                                            <a href={safeLink} target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1">
                                                <ExternalLink size={12}/> View Item
                                            </a>
                                        )}
                                    </div>
                                    {list.user.id === user.id ? (
                                        <span className="text-xs text-gray-400 italic">Bought status hidden</span>
                                    ) : item.boughtBy ? (
                                        <div className="flex flex-col items-end gap-1">
                                            <span className="flex items-center gap-1 text-xs font-bold text-xmas-green bg-green-100 px-2 py-1 rounded">
                                                <CheckCircle size={14}/> Bought by {item.boughtBy.name}
                                            </span>
                                            {(item.boughtBy.id === user.id || (user.children && user.children.some(c => c.id === item.boughtBy.id)) || isUserAdminOfThisGroup) && (
                                                <button
                                                    onClick={() => handleUnbuy(list.userId, item.id)}
                                                    className="text-xs text-blue-600 hover:underline"
                                                >
                                                    {isUserAdminOfThisGroup && item.boughtBy.id !== user.id && (!user.children || !user.children.some(c => c.id === item.boughtBy.id)) ? 'Clear' : 'Unbuy'}
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => handleBuyClick(list.userId, item)}
                                            className="btn-secondary py-1 px-3 text-sm"
                                        >
                                            <ShoppingCart size={14}/> Buy
                                        </button>
                                    )}
                                </div>
                              </div>
                            );
                          })}
                          {list.items.length === 0 && <p className="italic text-gray-400">This list is empty.</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FamilyWishlists;
